import { useMemo } from "react";
import { Alert, Button, Stack, Typography } from "@mui/material";

import { DataTable, EmptyState, Headline } from "@lopie/kernel-ui";
import Chart from "@lopie/kernel-ui/components/Chart";

import Basis from "./Basis.jsx";
import { describeMarks, drillSpec, marksFromPoint, marksFromRow } from "../lib/drill.js";
import { chartKind, describe, formatCell, shape } from "../lib/spec.js";

/** A chart's name in an audit row is its description, kept to what the kernel accepts. */
const FROM_MAX = 120;
const fromOf = (text) => (text.length > FROM_MAX ? `${text.slice(0, FROM_MAX - 1)}…` : text);

/**
 * What came back, drawn the way it can honestly be drawn.
 *
 * With `onDrill`, a mark, a point, a figure in the table and a row of a table
 * each open the records behind them: this works out *which* records as a spec
 * and hands it up, and the kernel decides what the person may then see. A mark
 * that cannot be turned into a spec (the chart already has all the filters the
 * kernel allows) hands up the reason instead, so it is said and not silent.
 */
export default function Answer({ result, state, schema, onDrill }) {
  const shaped = useMemo(() => shape(result, state), [result, state]);
  const title = describe(state, schema);
  const from = fromOf(title);

  const open = (marks) => {
    const out = drillSpec(state, schema, marks);
    onDrill(out.ok
      ? { spec: out.spec, title: describeMarks(marks) || title, from, type: state.type, fields: marks.map((m) => m.field) }
      : { refused: out.why, title: describeMarks(marks) || title });
  };

  if (shaped.kind === "empty") {
    return (
      <EmptyState title="Nothing to show">
        <Typography variant="body2">
          No records you can open match this. That does not mean none exist.
        </Typography>
        <Basis asOf={result.as_of} why={result.as_of_basis} />
        {result.linked ? <Typography variant="caption" color="text.secondary" role="note">{result.linked.basis}</Typography> : null}
      </EmptyState>
    );
  }

  return (
    <Stack sx={{ gap: 1 }}>
      {shaped.kind === "figures" ? (
        <Stack sx={{ gap: 2, alignItems: "flex-start" }}>
          {shaped.measures.map((m) => (
            <Headline
              key={m.key}
              eyebrow={title}
              figure={formatCell(m, shaped.row[m.key], shaped.row)}
              caption={m.label}
            />
          ))}
          {onDrill ? (
            <Button size="small" onClick={() => open([])}>Show the records</Button>
          ) : null}
        </Stack>
      ) : shaped.kind === "chart" ? (
        <Chart
          title={title}
          kind={chartKind(shaped, state)}
          dimension={shaped.dimension}
          series={shaped.series}
          rows={shaped.rows}
          valueFormat={shaped.valueFormat}
          asOf={result.as_of}
          onOpen={onDrill ? (point) => {
            const marks = marksFromPoint(shaped, state, schema, { index: point.index, seriesKey: point.seriesKey });
            if (marks) open(marks);
          } : undefined}
        />
      ) : (
        <Stack sx={{ gap: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{title}</Typography>
          {shaped.why ? <Typography variant="body2" color="text.secondary">{shaped.why}</Typography> : null}
          <DataTable
            columns={[
              ...shaped.columns.map((c) => ({
                key: c.key,
                label: c.label,
                align: c.role === "measure" ? "right" : "left",
                render: (row) => formatCell(c, row[c.key], row),
              })),
              ...(onDrill ? [{
                key: "__records",
                label: "Records",
                render: (row) => {
                  const marks = marksFromRow(shaped, state, schema, row);
                  const what = describeMarks(marks);
                  return (
                    <Button
                      size="small"
                      onClick={() => open(marks)}
                      aria-label={what ? `Show the records behind ${what}` : "Show the records"}
                    >
                      Show records
                    </Button>
                  );
                },
              }] : []),
            ]}
            rows={shaped.rows}
          />
        </Stack>
      )}
      {shaped.kind !== "chart" ? <Basis asOf={result.as_of} why={result.as_of_basis} /> : null}
      {result.linked ? (
        <Typography variant="caption" color="text.secondary" role="note">{result.linked.basis}</Typography>
      ) : null}
      {onDrill ? (
        <Typography variant="caption" color="text.secondary">
          Opening the records behind a mark is recorded in the audit log.
        </Typography>
      ) : null}
      {result.truncated ? (
        <Alert severity="info" variant="outlined">
          This is the first {result.limit.toLocaleString()} rows. Add a filter to narrow it.
        </Alert>
      ) : null}
    </Stack>
  );
}
